/**
 * Decision 0148 (a person's agents, amends 0142 §5): the shapes of "Your agents" (Account settings), the team's view
 * of each person's connected agents, and the pure helpers both the cloud and the web use (labels, default handles,
 * handle checks). A person's agents are their tools: ChatGPT, Every Agent, Claude, Claude Code or Codex, connected to
 * Minga over MCP, each reachable as `@<person>.<handle>`.
 */
import { Schema } from "effect"
import { Millis } from "./ids"

// ---- statuses, labels, handles -------------------------------------------------------------------------------------

/**
 * `active`: agents can message it. `muted`: its owner keeps it unreachable. `off`: disconnected (its OAuth grant
 * revoked), turned off by its owner, or its owner left the workspace.
 */
export const ParticipantStatus = Schema.Literals(["active", "muted", "off"])
export type ParticipantStatus = typeof ParticipantStatus.Type

/** Why a participant is off: its owner turned it off, disconnected its app, or left the workspace. */
export const ParticipantOffReason = Schema.Literals(["owner", "disconnect", "left"])
export type ParticipantOffReason = typeof ParticipantOffReason.Type

const CLIENT_LABELS: Readonly<Record<string, string>> = {
  "claude-code": "Claude Code",
  codex: "Codex",
  claude: "Claude",
  chatgpt: "ChatGPT",
  every: "Every Agent",
  // decision 0123: a browser agent through WebMCP on the Minga page
  webmcp: "Browser agent",
}

/**
 * A client's short name in words: `chatgpt` → "ChatGPT", `every` → "Every Agent", `claude-code` → "Claude Code";
 * any other (an unverified host's slug, a local client's name) as it is; `mcp` → "MCP".
 */
export const clientLabel = (client: string): string => {
  const c = client.trim()
  if (!c || c === "mcp") return "MCP"
  return CLIENT_LABELS[c] ?? c
}

/** A handle: lowercase letters, digits, `-` and `_`, starting with a letter or digit, at most 32 characters. */
export const PARTICIPANT_HANDLE = /^[a-z0-9][a-z0-9_-]{0,31}$/

/** A handle from free text (`Claude Code` → `claude-code`); `fallback` when nothing is left. */
export const handleSlug = (text: string, fallback = "session"): string => {
  const s = text
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[-_]+|[-_]+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 32)
    .replace(/[-_]+$/g, "")
  return s.length > 0 ? s : fallback
}

/**
 * The handle a client gets when it first calls Minga (decision 0148 §2): `dot` for ChatGPT (the person's assistant),
 * else the client's short name (`every`, `claude`, `claude-code`, `codex`, an unverified host's slug such as
 * `agent-example-com`, a local client's name).
 */
export const defaultParticipantHandle = (client: string): string =>
  client === "chatgpt" ? "dot" : handleSlug(client, "agent")

/** A handle the owner typed, checked: the normalised handle, or what is wrong in words. */
export const checkParticipantHandle = (
  raw: string,
): { readonly ok: true; readonly handle: string } | { readonly ok: false; readonly reason: string } => {
  const h = raw.trim().replace(/^@/, "").toLowerCase()
  if (h.length === 0) return { ok: false, reason: "Give it a handle." }
  if (h.length > 32) return { ok: false, reason: "At most 32 characters." }
  if (!PARTICIPANT_HANDLE.test(h))
    return {
      ok: false,
      reason: "Use lowercase letters, digits, - and _, starting with a letter or digit.",
    }
  return { ok: true, handle: h }
}

// ---- GET /api/me/agents --------------------------------------------------------------------------------------------

/** One participant of one of the person's apps, across the workspaces that hold it. */
export const MyParticipant = Schema.Struct({
  id: Schema.String,
  kind: Schema.Literals(["agent", "session"]),
  handle: Schema.String,
  status: ParticipantStatus,
  offReason: Schema.NullOr(ParticipantOffReason),
  seenAt: Millis,
  /** The workspaces it is reachable in (an agent: every workspace, or the one it was connected to). */
  workspaces: Schema.Array(Schema.Struct({ id: Schema.String, name: Schema.String })),
  /** A session: the project whose repo it works in (null when the person can't see the project any more). */
  project: Schema.NullOr(
    Schema.Struct({ id: Schema.String, name: Schema.String, workspaceId: Schema.String }),
  ),
  branch: Schema.NullOr(Schema.String),
})
export type MyParticipant = typeof MyParticipant.Type

/** One of the person's apps: an OAuth client they allowed (or one that is gone but still has participants). */
export const MyAgentClient = Schema.Struct({
  clientId: Schema.String,
  /** The short name (`chatgpt`, `every`, `claude`, `claude-code`, `codex`, a host slug). */
  client: Schema.String,
  /** "ChatGPT", "Claude (claude.ai)", the host(s) of an unverified app, a local client's registered name. */
  name: Schema.String,
  trust: Schema.NullOr(Schema.Literals(["verified", "local", "unverified"])),
  /** An unverified app's registered name ("calls itself …"), else null. */
  calls: Schema.NullOr(Schema.String),
  /** The consent row (Disconnect); null when the app is disconnected. */
  consentId: Schema.NullOr(Schema.String),
  connectedAt: Schema.NullOr(Millis),
  /** It keeps a refresh token (`offline_access`). */
  staysSignedIn: Schema.Boolean,
  lastSeenAt: Schema.NullOr(Millis),
  participants: Schema.Array(MyParticipant),
  /** Projects it gets `message.received` webhooks from (MCP Events subscriptions), with the `to` it asked for. */
  receives: Schema.Array(
    Schema.Struct({
      projectId: Schema.String,
      projectName: Schema.String,
      workspaceId: Schema.String,
      to: Schema.String,
      active: Schema.Boolean,
    }),
  ),
})
export type MyAgentClient = typeof MyAgentClient.Type

export const MyAgents = Schema.Struct({
  clients: Schema.Array(MyAgentClient),
  /** Minga's MCP URL (`<origin>/mcp`), for "Connect an agent". */
  mcpUrl: Schema.String,
})
export type MyAgents = typeof MyAgents.Type

/** `PATCH /api/me/agents/participants/:id`: rename and/or mute (owner only). */
export const UpdateParticipantBody = Schema.Struct({
  handle: Schema.optionalKey(Schema.String),
  muted: Schema.optionalKey(Schema.Boolean),
})
export type UpdateParticipantBody = typeof UpdateParticipantBody.Type

// ---- GET /api/workspaces/:ws/participants --------------------------------------------------------------------------

/**
 * A member's connected agent as their teammates see it: that it exists, its handle, client and status, when it was
 * last seen. Never its conversations with its owner.
 */
export const TeamParticipant = Schema.Struct({
  id: Schema.String,
  ownerId: Schema.String,
  kind: Schema.Literals(["agent", "session"]),
  handle: Schema.String,
  client: Schema.String,
  status: ParticipantStatus,
  seenAt: Millis,
  /** A session's project and branch (only in a project's list). */
  projectId: Schema.NullOr(Schema.String),
  branch: Schema.NullOr(Schema.String),
})
export type TeamParticipant = typeof TeamParticipant.Type

export const TeamParticipants = Schema.Struct({ participants: Schema.Array(TeamParticipant) })
export type TeamParticipants = typeof TeamParticipants.Type
